import { pvDebug } from '../utils/helpers';
import type { Cart, CartItem } from '@/scripts/shopify';
import { getTriggeredOfferRules, type OfferRule } from '@/scripts/core/product-cache';

type BnplElData = {
  title: string;
  autoRenewText: string;
}

type ProductType = 'bundle' | 'other';

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const productTypeTitle: Record<ProductType, string> = {
  bundle: 'This bundle includes:',
  other: 'Included with purchase:'
}

function processItem(item: CartItem, doc: HTMLElement): void {
  pvDebug(
    'cart-bnpl:renderBNPLMemberships:processItem:start',
    {
      item,
    },
  );

  const itemEl = doc.querySelector<HTMLDivElement>(`[data-item-key="${item.key}"]`);
  const productType = itemEl?.dataset.productType as ProductType;
  const bnplEl = itemEl?.querySelector<HTMLDivElement>('.cart-bnpl-item');
  pvDebug(
    'cart-bnpl:renderBNPLMemberships:processItem:type',
    {
      itemEl,
      bnplEl,
      productType,
    },
  );

  if (bnplEl !== null) {
    pvDebug('cart-bnpl:renderBNPLMemberships:processItem:found');
    return;
  }

  const offers = getTriggeredOfferRules(item.product_id);
  pvDebug(
    'cart-bnpl:renderBNPLMemberships:processItem:offers',
    {
      offers,
    },
  );

  if (offers.length === 0) {
    pvDebug('cart-bnpl:renderBNPLMemberships:processItem:no-offers');
    return;
  }

  const filteredOffers = offers.filter((offer) => ['bundle_membership', 'alcm'].includes(offer.type));
  const data = getMembershipData(filteredOffers[0]);
  const newBnplItem = createBnplItemEl(data, productType);
  itemEl?.appendChild(newBnplItem);

  pvDebug('cart-bnpl:renderBNPLMemberships:processItem:end');
}

function getMembershipData(offer: OfferRule): BnplElData {
  const handle = offer.giftProduct.handle.toLowerCase();
  const title = offer.giftProduct.title;
  const isOneTime = handle.includes('-ot');
  const price = currency.format(offer.giftProduct.price / 100);
  const duration = offer.giftProduct.membershipDuration?.replace('_month', ' mo');
  let autoRenewText = '';

  if (!isOneTime) {
    autoRenewText = `Auto-renews at ${price}/${duration} — cancel anytime`;
  }

  return {
    title,
    autoRenewText,
  }
}

function createBnplItemEl(data: BnplElData, productType: ProductType): HTMLDivElement {
  pvDebug(
    'cart-bnpl:renderBNPLMemberships:createBnplItemEl:start',
    {
      data,
      productType,
    }
  );

  let type = productType;
  if (productType !== 'bundle') type = 'other';
  const descriptor = productTypeTitle[type];
  let autoRenewHtml = '';
  if (data.autoRenewText !== '') {
    autoRenewHtml = `
      <div class="cart-free_product-purchase">
        ${data.autoRenewText}
      </div>
    `;
  }

  const el = document.createElement('div');
  el.classList.add('cart-free_product', 'cart-bnpl-item');
  el.innerHTML = `
    <div class="cart-free_product-info">
      <div class="cart-free_product-content">
        <div class="cart-free_product-bundle">
          ${descriptor}
        </div>
        <div class="cart-free_product-details">
          <div class="cart-free_product-check">
            <span>
              <svg
                class="icon-checkmark"
                aria-hidden="true"
                focusable="false"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 12 9"
                fill="none"
              >
                <path fill-rule="evenodd" clip-rule="evenodd" d="M11.35.643a.5.5 0 01.006.707l-6.77 6.886a.5.5 0 01-.719-.006L.638 4.845a.5.5 0 11.724-.69l2.872 3.011 6.41-6.517a.5.5 0 01.707-.006h-.001z" fill="currentColor"/>
              </svg>
            </span>
          </div>
          <div>
            <div class="cart-free_product-title">
              <span>${data.title}</span>
            </div>
            <div class="cart-free_product-footer">
              ${autoRenewHtml}
            </div>
          </div>
        </div>
        <div class="cart-free_product-bnpl_notice">
            You will receive an email after checkout with instructions on how to activate your membership.
        </div>
      </div>
    </div>
  `;

  pvDebug(
    'cart-bnpl:renderBNPLMemberships:createBnplItemEl:end',
    {
      type,
      descriptor,
      autoRenewHtml,
      el,
    },
  );

  return el;
}

export function getCart(ref?: HTMLElement): Cart | undefined {
  const container = ref ?? document;
  const elId = '#pv-cart-json';
  const el = container.querySelector<HTMLScriptElement>(elId);
  if (!el?.textContent) {
    pvDebug(
      'cart-bnpl:cart-parse:not-found',
      {
        scriptEl: elId,
      },
    );
    return;
  }

  try {
    pvDebug('cart-bnpl:cart-parse:start');
    const cart = JSON.parse(el.textContent);
    pvDebug(
      'cart-bnpl:cart-parse:parsed',
      {
        cart
      },
    );

    // Remove element from DOM, only need it on load.
    el.remove();

    return cart;
  } catch (e: any) {
    pvDebug('cart-bnpl:cart-parse:exception');
    return;
  }
}

export function renderBNPLMemberships(
  items: CartItem[],
  ref?: HTMLElement,
): void {
  pvDebug(
    'cart-bnpl:renderBNPLMemberships:start',
    {
      items,
    }
  );

  if (items.length === 0) return;

  const container = ref ?? document.documentElement;
  for (const item of items) {
    processItem(item, container);
  }
}
